import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import PdfLessonService from '../services/PdfLessonService';
import AdminService from '../services/AdminService';
import TeacherCurriculumService from '../services/TeacherCurriculumService';
import toast from 'react-hot-toast';
import { FaFilePdf, FaSpinner, FaMagic, FaCheckCircle, FaTimes } from 'react-icons/fa';
import RichTextEditor from './RichTextEditor';
import './PdfLessonUploader.css';

const PdfLessonUploader = ({ unitId: initialUnitId, leagueId, isTeacherCurriculum = false, onLessonCreated, onClose, availableUnits = [] }) => {
  const [unitId, setUnitId] = useState(initialUnitId || '');
  
  useEffect(() => {
    if (initialUnitId) {
      setUnitId(initialUnitId);
    }
  }, [initialUnitId]);
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Edit Lesson, 4: Edit Quiz
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pdfText, setPdfText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [lessonContent, setLessonContent] = useState('');
  const [lessonTitle, setLessonTitle] = useState('');
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [generateQuiz, setGenerateQuiz] = useState(true);
  const [numQuestions, setNumQuestions] = useState(5);
  const [useVision, setUseVision] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        toast.error('Please select a PDF file');
        return;
      }
      if (selectedFile.size > 20 * 1024 * 1024) {
        toast.error('File size must be less than 20MB');
        return;
      }
      setFile(selectedFile);
      setLessonTitle(selectedFile.name.replace('.pdf', ''));
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a PDF file');
      return;
    }

    try {
      setUploading(true);
      const result = await PdfLessonService.uploadPdf(file, useVision);
      setPdfText(result.text);
      setStep(2);
      const method = result.extractionMethod === 'vision-based' ? ' (AI Vision)' : '';
      toast.success(`PDF uploaded successfully! Extracted ${result.numPages} pages${method}.`);
    } catch (error) {
      toast.error(error.message || 'Failed to upload PDF');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateContent = async () => {
    if (!pdfText || !pdfText.trim()) {
      toast.error('No PDF text available');
      return;
    }

    try {
      setGenerating(true);
      toast.loading('Generating enhanced lesson and quiz... This may take a minute.', { id: 'generating' });

      if (generateQuiz) {
        const result = await PdfLessonService.generateAll(
          pdfText, 
          lessonTitle || null, 
          numQuestions || 5,
          customPrompt && customPrompt.trim() ? customPrompt.trim() : null
        );
        if (result.lesson && result.lesson.content) {
          setLessonContent(result.lesson.content);
        } else if (result.content) {
          setLessonContent(result.content);
        }
        if (result.quiz && result.quiz.questions) {
          setQuizQuestions(result.quiz.questions);
          toast.success(`Generated lesson and ${result.quiz.questions.length} quiz questions!`, { id: 'generating' });
        } else {
          setQuizQuestions([]);
          toast.success('Generated lesson!', { id: 'generating' });
        }
      } else {
        const result = await PdfLessonService.generateLesson(
          pdfText, 
          lessonTitle || null,
          customPrompt && customPrompt.trim() ? customPrompt.trim() : null
        );
        if (result.content) {
          setLessonContent(result.content);
        }
        setQuizQuestions([]);
        toast.success('Generated enhanced lesson!', { id: 'generating' });
      }

      setStep(3);
    } catch (error) {
      toast.error(error.message || 'Failed to generate content', { id: 'generating' });
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveLesson = async () => {
    if (!lessonContent.trim()) {
      toast.error('Lesson content cannot be empty');
      return;
    }

    if (isTeacherCurriculum) {
      if (!leagueId) {
        toast.error('League ID is required');
        return;
      }
    } else {
      if (!unitId) {
        toast.error('Unit ID is required');
        return;
      }
    }

    try {
      setSaving(true);
      
      if (isTeacherCurriculum) {
        // For teacher curriculum, create lesson directly
        const lessonData = {
          title: lessonTitle,
          content: lessonContent,
          order: 0,
          xpValue: 50,
          isActive: true
        };
        
        const createdLesson = await TeacherCurriculumService.createLesson(leagueId, lessonData);
        
        // Create quiz if generated
        if (generateQuiz && quizQuestions && quizQuestions.length > 0) {
          const sanitizedQuestions = quizQuestions.map(q => {
            const sanitized = { ...q };
            if (sanitized.options && Array.isArray(sanitized.options)) {
              sanitized.options = sanitized.options.map(opt => {
                if (typeof opt === 'string' && opt.includes('<')) {
                  return opt.replace(/<[^>]*>/g, '').trim();
                }
                return String(opt).trim();
              });
            }
            if (sanitized.type === 'multiple_choice') {
              sanitized.correctAnswer = parseInt(sanitized.correctAnswer) || 0;
            } else if (sanitized.type === 'true_false') {
              sanitized.correctAnswer = sanitized.correctAnswer === true || sanitized.correctAnswer === 'true';
            }
            return sanitized;
          });
          
          const quizData = {
            lessonId: createdLesson.lesson.id,
            title: `Quiz: ${lessonTitle}`,
            description: 'Auto-generated quiz from PDF content',
            type: 'mixed',
            questions: sanitizedQuestions,
            passingScore: 70,
            xpValue: 25,
            isActive: true
          };
          
          await TeacherCurriculumService.createQuiz(leagueId, quizData);
        }
        
        toast.success('Lesson created and saved successfully!');
        if (onLessonCreated) {
          onLessonCreated(createdLesson.lesson);
        }
      } else {
        // For regular lessons, use the existing flow
        const shouldGenerateQuiz = generateQuiz;
        const result = await PdfLessonService.createLessonFromPdf(
          file,
          unitId,
          lessonTitle,
          shouldGenerateQuiz,
          numQuestions,
          customPrompt && customPrompt.trim() ? customPrompt.trim() : null
        );

        // Update the lesson with edited content
        if (result.lesson && result.lesson.id) {
          await AdminService.updateLesson(result.lesson.id, {
            title: lessonTitle,
            content: lessonContent
          });
        }

        // Update quiz if it was generated and we have edited questions
        if (result.quiz && result.quiz.id && quizQuestions && quizQuestions.length > 0) {
          const sanitizedQuestions = quizQuestions.map(q => {
            const sanitized = { ...q };
            if (sanitized.options && Array.isArray(sanitized.options)) {
              sanitized.options = sanitized.options.map(opt => {
                if (typeof opt === 'string' && opt.includes('<')) {
                  return opt.replace(/<[^>]*>/g, '').trim();
                }
                return String(opt).trim();
              });
            }
            if (sanitized.type === 'multiple_choice') {
              sanitized.correctAnswer = parseInt(sanitized.correctAnswer) || 0;
            } else if (sanitized.type === 'true_false') {
              sanitized.correctAnswer = sanitized.correctAnswer === true || sanitized.correctAnswer === 'true';
            }
            return sanitized;
          });
          
          await AdminService.updateQuiz(result.quiz.id, {
            questions: sanitizedQuestions
          });
        }

        toast.success('Lesson created and saved successfully!');
        if (onLessonCreated) {
          onLessonCreated(result.lesson);
        }
      }
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save lesson');
    } finally {
      setSaving(false);
    }
  };

  const handleQuizQuestionChange = (index, field, value) => {
    if (!quizQuestions || index < 0 || index >= quizQuestions.length) {
      return;
    }
    const updated = [...quizQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setQuizQuestions(updated);
  };

  const handleAddQuizOption = (questionIndex) => {
    const updated = [...quizQuestions];
    if (updated[questionIndex]) {
      if (!updated[questionIndex].options) {
        updated[questionIndex].options = [];
      }
      updated[questionIndex].options.push('');
      setQuizQuestions(updated);
    }
  };

  const handleRemoveQuizOption = (questionIndex, optionIndex) => {
    if (!quizQuestions || questionIndex < 0 || questionIndex >= quizQuestions.length) {
      return;
    }
    const updated = [...quizQuestions];
    if (updated[questionIndex] && updated[questionIndex].options && Array.isArray(updated[questionIndex].options)) {
      updated[questionIndex].options.splice(optionIndex, 1);
      if (updated[questionIndex].correctAnswer >= updated[questionIndex].options.length) {
        updated[questionIndex].correctAnswer = 0;
      }
    }
    setQuizQuestions(updated);
  };

  return (
    <div className="pdf-lesson-uploader">
      <div className="pdf-uploader-header">
        <h2>Create Lesson from PDF</h2>
        {onClose && (
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
        )}
      </div>

      {/* Step 1: Upload PDF */}
      {step === 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="upload-step"
        >
          {!isTeacherCurriculum && availableUnits && availableUnits.length > 0 && (
            <div className="form-group">
              <label>Select Unit *</label>
              <select
                value={unitId || ''}
                onChange={(e) => setUnitId(e.target.value)}
                required
              >
                <option value="">Select a unit</option>
                {availableUnits.map(unit => (
                  <option key={unit.id} value={unit.id}>
                    {unit.courseTitle || ''} - {unit.title || `Unit ${unit.id}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {isTeacherCurriculum && (
            <div className="form-group">
              <label>League</label>
              <input
                type="text"
                value={leagueId || ''}
                disabled
                className="disabled-input"
                placeholder="Lesson will be created in the selected league"
              />
            </div>
          )}
          
          <div className="upload-area" onClick={() => document.getElementById('pdf-input').click()}>
            <FaFilePdf className="upload-icon" />
            <p>Click to select a PDF file</p>
            <p className="upload-hint">or drag and drop</p>
            {file && (
              <div className="selected-file">
                <FaFilePdf />
                <span>{file.name}</span>
                <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
              </div>
            )}
          </div>
          <input
            id="pdf-input"
            type="file"
            accept="application/pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          <div className="form-group">
            <label>Lesson Title (optional)</label>
            <input
              type="text"
              value={lessonTitle}
              onChange={(e) => setLessonTitle(e.target.value)}
              placeholder="Leave empty to auto-generate from PDF"
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={generateQuiz}
                onChange={(e) => setGenerateQuiz(e.target.checked)}
              />
              Generate quiz automatically
            </label>
            {generateQuiz && (
              <div style={{ marginTop: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  Number of Quiz Questions:
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={numQuestions}
                  onChange={(e) => setNumQuestions(parseInt(e.target.value) || 5)}
                  placeholder="Number of questions"
                  className="num-questions-input"
                />
              </div>
            )}
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={useVision}
                onChange={(e) => setUseVision(e.target.checked)}
              />
              Use AI Vision Extraction (for scanned PDFs or PDFs with images)
            </label>
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginLeft: '24px' }}>
              Enable this if your PDF is scanned or contains images with text. Uses GPT-4 Vision to extract text from images.
            </p>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={showAdvancedOptions}
                onChange={(e) => setShowAdvancedOptions(e.target.checked)}
              />
              Show Advanced Options
            </label>
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginLeft: '24px' }}>
              Add custom instructions or preferences for AI lesson generation
            </p>
          </div>

          {showAdvancedOptions && (
            <div className="form-group" style={{ 
              background: '#f9fafb', 
              padding: '16px', 
              borderRadius: '8px', 
              border: '1px solid #e5e7eb',
              marginTop: '8px'
            }}>
              <label style={{ marginBottom: '8px', display: 'block', fontWeight: '500' }}>
                Custom AI Instructions / Preferences (Optional)
              </label>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Enter your custom instructions or preferences for the AI lesson generator. For example:&#10;- Focus on practical examples&#10;- Include more visual descriptions&#10;- Emphasize key formulas&#10;- Use simpler language for beginners&#10;- Add more real-world applications"
                rows={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                These instructions will be passed to the AI to customize how the lesson is generated. Leave empty to use default settings.
              </p>
            </div>
          )}

          <button
            className="btn-primary"
            onClick={handleUpload}
            disabled={!file || uploading || (!isTeacherCurriculum && availableUnits && availableUnits.length > 0 && !unitId)}
          >
            {uploading ? (
              <>
                <FaSpinner className="spinner" />
                Uploading...
              </>
            ) : (
              <>
                <FaFilePdf />
                Upload PDF
              </>
            )}
          </button>
        </motion.div>
      )}

      {/* Step 2: Preview PDF Text */}
      {step === 2 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="preview-step"
        >
          <h3>PDF Text Preview</h3>
          <div className="pdf-text-preview">
            <p>{pdfText.substring(0, 1000)}...</p>
            <p className="text-muted">({pdfText.length} characters extracted)</p>
          </div>
          
          <div className="form-group">
            <label>Lesson Title</label>
            <input
              type="text"
              value={lessonTitle}
              onChange={(e) => setLessonTitle(e.target.value)}
              placeholder="Enter lesson title"
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={showAdvancedOptions}
                onChange={(e) => setShowAdvancedOptions(e.target.checked)}
              />
              Show Advanced Options
            </label>
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginLeft: '24px' }}>
              Add custom instructions or preferences for AI lesson generation
            </p>
          </div>

          {showAdvancedOptions && (
            <div className="form-group" style={{ 
              background: '#f9fafb', 
              padding: '16px', 
              borderRadius: '8px', 
              border: '1px solid #e5e7eb',
              marginTop: '8px'
            }}>
              <label style={{ marginBottom: '8px', display: 'block', fontWeight: '500' }}>
                Custom AI Instructions / Preferences (Optional)
              </label>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Enter your custom instructions or preferences for the AI lesson generator. For example:&#10;- Focus on practical examples&#10;- Include more visual descriptions&#10;- Emphasize key formulas&#10;- Use simpler language for beginners&#10;- Add more real-world applications"
                rows={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                These instructions will be passed to the AI to customize how the lesson is generated. Leave empty to use default settings.
              </p>
            </div>
          )}

          <button
            className="btn-primary"
            onClick={handleGenerateContent}
            disabled={generating}
          >
            {generating ? (
              <>
                <FaSpinner className="spinner" />
                Generating...
              </>
            ) : (
              <>
                <FaMagic />
                Generate Enhanced Lesson {generateQuiz && '& Quiz'}
              </>
            )}
          </button>

          <button
            className="btn-secondary"
            onClick={() => setStep(1)}
            disabled={generating}
          >
            Back
          </button>
        </motion.div>
      )}

      {/* Step 3: Edit Lesson */}
      {step === 3 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="edit-lesson-step"
        >
          <h3>Edit Lesson Content</h3>
          
          <div className="form-group">
            <label>Lesson Title</label>
            <input
              type="text"
              value={lessonTitle}
              onChange={(e) => setLessonTitle(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Lesson Content</label>
            <RichTextEditor
              value={lessonContent}
              onChange={setLessonContent}
              placeholder="Edit your lesson content..."
            />
          </div>

          <div className="button-group">
            <button
              className="btn-secondary"
              onClick={() => setStep(2)}
            >
              Back
            </button>
            {generateQuiz && quizQuestions && quizQuestions.length > 0 ? (
              <button
                className="btn-primary"
                onClick={() => setStep(4)}
              >
                Next: Edit Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleSaveLesson}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <FaSpinner className="spinner" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaCheckCircle />
                    Save Lesson
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* Step 4: Edit Quiz */}
      {step === 4 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="edit-quiz-step"
        >
          <h3>Edit Quiz Questions</h3>
          
          {quizQuestions && quizQuestions.length > 0 ? quizQuestions.map((question, qIndex) => (
            <div key={qIndex} className="quiz-question-editor">
              <div className="form-group">
                <label>Question {qIndex + 1}</label>
                <RichTextEditor
                  value={question.question}
                  onChange={(value) => handleQuizQuestionChange(qIndex, 'question', value)}
                  placeholder="Enter question text..."
                  compact={true}
                />
              </div>

              <div className="form-group">
                <label>Question Type</label>
                <select
                  value={question.type}
                  onChange={(e) => handleQuizQuestionChange(qIndex, 'type', e.target.value)}
                >
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="true_false">True/False</option>
                  <option value="short_answer">Short Answer</option>
                </select>
              </div>

              {question.type === 'multiple_choice' && (
                <>
                  <div className="form-group">
                    <label>Options</label>
                    {question.options?.map((option, oIndex) => (
                      <div key={oIndex} className="option-row">
                        <input
                          type="radio"
                          name={`correct-${qIndex}`}
                          checked={question.correctAnswer === oIndex}
                          onChange={() => handleQuizQuestionChange(qIndex, 'correctAnswer', oIndex)}
                        />
                        <input
                          type="text"
                          value={typeof option === 'string' ? option.replace(/<[^>]*>/g, '') : option}
                          onChange={(e) => {
                            const updated = [...question.options];
                            updated[oIndex] = e.target.value;
                            handleQuizQuestionChange(qIndex, 'options', updated);
                          }}
                          placeholder={`Option ${oIndex + 1}`}
                          className="option-input"
                        />
                        <button
                          className="btn-remove"
                          onClick={() => handleRemoveQuizOption(qIndex, oIndex)}
                          type="button"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                    <button
                      className="btn-add-option"
                      onClick={() => handleAddQuizOption(qIndex)}
                      type="button"
                    >
                      + Add Option
                    </button>
                  </div>
                </>
              )}

              {question.type === 'true_false' && (
                <div className="form-group">
                  <label>Correct Answer</label>
                  <select
                    value={question.correctAnswer}
                    onChange={(e) => handleQuizQuestionChange(qIndex, 'correctAnswer', e.target.value === 'true')}
                  >
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                </div>
              )}

              {question.type === 'short_answer' && (
                <div className="form-group">
                  <label>Correct Answer</label>
                  <input
                    type="text"
                    value={question.correctAnswer || ''}
                    onChange={(e) => handleQuizQuestionChange(qIndex, 'correctAnswer', e.target.value)}
                    placeholder="Enter correct answer"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Explanation (optional)</label>
                <RichTextEditor
                  value={question.explanation || ''}
                  onChange={(value) => handleQuizQuestionChange(qIndex, 'explanation', value)}
                  placeholder="Explain why this is the correct answer..."
                  compact={true}
                />
              </div>
            </div>
          )) : (
            <p className="text-muted">No quiz questions available. Please generate a quiz first.</p>
          )}

          <div className="button-group">
            <button
              className="btn-secondary"
              onClick={() => setStep(3)}
            >
              Back
            </button>
            <button
              className="btn-primary"
              onClick={handleSaveLesson}
              disabled={saving}
            >
              {saving ? (
                <>
                  <FaSpinner className="spinner" />
                  Saving...
                </>
              ) : (
                <>
                  <FaCheckCircle />
                  Save Lesson & Quiz
                </>
              )}
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default PdfLessonUploader;

